import React, { useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner, faTimesCircle } from "@fortawesome/free-solid-svg-icons";
import api from "../services/api";
import RepairProjectHierarchySelector from "./RepairProjectHierarchySelector";

export const normalizeIds = (ids) =>
  Array.from(
    new Set(
      (Array.isArray(ids) ? ids : [])
        .map((id) => Number(id))
        .filter((id) => Number.isInteger(id) && id > 0),
    ),
  );

const parseBridgeJson = (value, fallback) => {
  if (!value) return fallback;
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch (error) {
    return fallback;
  }
};

export const getEnabledLevels = (bridge) => {
  const levels = parseBridgeJson(bridge?.enabledLevels, [1, 2, 3]);
  return Array.isArray(levels) ? levels : [1, 2, 3];
};

export const getAreaDataLevel = (bridge) => {
  const levelLabels = parseBridgeJson(bridge?.levelLabels, {});
  for (let level = 1; level <= 8; level += 1) {
    if (levelLabels?.[`level${level}`]?.holdsAreaData) {
      return level;
    }
  }
  return 2;
};

const getRawLevelLabel = (bridge, level) => {
  const levelLabels = parseBridgeJson(bridge?.levelLabels, {});
  return levelLabels?.[`level${level}`]?.label || `Level ${level}`;
};

const getDisplayLevelLabel = (bridge, level) => {
  const label = getRawLevelLabel(bridge, level);
  return label === "Direction" ? "Item" : label;
};

export const getAreaEntityLabel = (bridge) => {
  const label = String(getRawLevelLabel(bridge, getAreaDataLevel(bridge)) || "Element").trim();
  if (!label) return "Elements";
  return label.toLowerCase().endsWith("s") ? label : `${label}s`;
};

const summarizeValues = (values) => {
  const uniqueValues = Array.from(
    new Set(
      (values || [])
        .map((value) => String(value || "").trim())
        .filter(Boolean)
        .filter((value) => value !== "-"),
    ),
  );

  if (uniqueValues.length === 0) return "-";
  if (uniqueValues.length === 1) return uniqueValues[0];

  const visibleValues = uniqueValues.slice(0, 4);
  const suffix = uniqueValues.length > visibleValues.length ? `, +${uniqueValues.length - visibleValues.length}` : "";
  return `[${visibleValues.join(", ")}${suffix}]`;
};

const getNodeName = (node) =>
  node?.name || node?.element_name || node?.code || node?.element_code || "";

export const flattenHierarchyRows = (roots) => {
  const rows = [];

  const visit = (node, context = {}) => {
    const levelNumber = node.levelNumber || node.level_number;
    const currentContext = { ...context };

    if (levelNumber != null) {
      currentContext[`level${levelNumber}`] = getNodeName(node);
    }

    if (node.direction && !currentContext.direction) {
      currentContext.direction = node.direction;
    }
    if (node.position && !currentContext.position) {
      currentContext.position = node.position;
    }

    if (node.isSelected) {
      rows.push({
        id: node.id,
        direction: currentContext.direction || "-",
        position: currentContext.position || "-",
        levels: { ...currentContext },
        area: parseFloat(node.area || node.total_area || 0) || 0,
      });
    }

    (node.children || []).forEach((child) => visit(child, currentContext));
  };

  (roots || []).forEach((root) => visit(root, {}));
  rows.sort((left, right) =>
    String(left.id).localeCompare(String(right.id), undefined, { numeric: true }),
  );
  return rows;
};

export const buildPathSummary = (rows, bridge, elementCount) => {
  const areaDataLevel = getAreaDataLevel(bridge);
  const areaEntityLabel = getAreaEntityLabel(bridge).toLowerCase();
  const levels = getEnabledLevels(bridge)
    .filter((level) => level <= areaDataLevel)
    .sort((left, right) => left - right);

  if (!rows.length) {
    return `${elementCount} ${areaEntityLabel}`;
  }

  const pathTokens = levels
    .filter((level) => level < areaDataLevel)
    .map((level) => {
      const values = rows.map((row) =>
        row.levels?.[`level${level}`] ||
        (level === 1 ? row.direction : level === 2 ? row.position : ""),
      );
      return summarizeValues(values);
    })
    .filter(Boolean);

  pathTokens.push(`${rows.length} ${areaEntityLabel}`);
  return pathTokens.join(" > ");
};

const estimateModalWidth = (bridge) => {
  const areaDataLevel = getAreaDataLevel(bridge);
  const visibleLevels = getEnabledLevels(bridge)
    .filter((level) => level <= areaDataLevel)
    .filter((level) => {
      const label = getDisplayLevelLabel(bridge, level);
      return (
        (label !== "Item" && label !== "Direction" && label !== "Position") ||
        level === areaDataLevel
      );
    });
  const columnCount = 2 + Math.max(visibleLevels.length, 1);
  return `min(96vw, ${Math.max(760, columnCount * 152 + 160)}px)`;
};

const BridgeSelectionModal = ({
  isOpen,
  bridgeId,
  selectedBridge,
  initialSelectedIds = [],
  onClose,
  onSave,
  title = "Bridge Selection",
  saveLabel = "Save Selection",
}) => {
  const normalizedInitialIds = useMemo(
    () => normalizeIds(initialSelectedIds),
    [initialSelectedIds],
  );
  const initialSignature = normalizedInitialIds.join(",");
  const [currentSelectedIds, setCurrentSelectedIds] = useState(normalizedInitialIds);
  const [currentSelectedElements, setCurrentSelectedElements] = useState([]);
  const [saving, setSaving] = useState(false);
  const [localError, setLocalError] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    const resetIds = initialSignature
      ? initialSignature.split(",").map((id) => Number(id))
      : [];
    setCurrentSelectedIds(resetIds);
    setCurrentSelectedElements([]);
    setSaving(false);
    setLocalError(null);
  }, [isOpen, initialSignature]);

  if (!isOpen || !bridgeId) {
    return null;
  }

  const handleSelectionChange = (ids, elements) => {
    setCurrentSelectedIds(normalizeIds(ids));
    setCurrentSelectedElements(Array.isArray(elements) ? elements : []);
    setLocalError(null);
  };

  const handleSave = async () => {
    const elementIds = normalizeIds(currentSelectedIds);
    if (elementIds.length === 0) {
      setLocalError("Select at least one item before saving.");
      return;
    }

    try {
      setSaving(true);
      setLocalError(null);

      const response = await api.post("/new-simulation/hierarchy-structure", {
        bridgeId: Number(bridgeId),
        elementIds,
      });

      const hierarchy = response.data?.success ? response.data?.data?.hierarchy : [];
      const rows = flattenHierarchyRows(hierarchy);
      const rowArea = rows.reduce((sum, row) => sum + (Number(row.area) || 0), 0);
      const cachedArea = currentSelectedElements.reduce((sum, element) => {
        const area = parseFloat(element?.total_area ?? element?.area ?? 0);
        return Number.isNaN(area) ? sum : sum + area;
      }, 0);
      const area = rowArea || cachedArea;

      onSave?.({
        elementIds,
        elements: currentSelectedElements,
        rows,
        area,
        pathSummary: buildPathSummary(rows, selectedBridge, elementIds.length),
        areaEntityLabel: getAreaEntityLabel(selectedBridge),
      });
    } catch (error) {
      console.error("Error saving bridge selection:", error);
      setLocalError(
        error.response?.data?.message || "Selection could not be saved. Please try again.",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4 py-6">
      <div
        className="flex max-h-[92vh] flex-col overflow-hidden rounded-lg bg-white shadow-xl"
        style={{ width: estimateModalWidth(selectedBridge) }}
      >
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
            <p className="mt-1 text-sm text-gray-500">
              Choose the hierarchy items for this saved selection.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
            aria-label="Close bridge selection dialog"
          >
            <FontAwesomeIcon icon={faTimesCircle} className="text-2xl" />
          </button>
        </div>

        <div className="overflow-y-auto bg-gray-50 p-4">
          {localError && (
            <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {localError}
            </div>
          )}
          <RepairProjectHierarchySelector
            bridgeId={bridgeId}
            initialSelectedIds={normalizedInitialIds}
            onSelectionChange={handleSelectionChange}
            showTopFilters={false}
            showSelectionSummary={false}
            clearSelectionOnBranchChange
          />
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-gray-200 bg-white px-6 py-4">
          <div className="text-sm text-gray-600">
            {currentSelectedIds.length} {getAreaEntityLabel(selectedBridge).toLowerCase()} selected
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="rounded-md bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || currentSelectedIds.length === 0}
              className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {saving && <FontAwesomeIcon icon={faSpinner} spin />}
              {saving ? "Saving..." : saveLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BridgeSelectionModal;
